/**
 * useTextResult — TanStack Query hook fetching a stored markdown result for
 * a SkuRun the caller owns.
 *
 * Endpoint: GET /api/v1/me/result/:id
 * Response: { title: string | null, markdown: string }
 * Returns 404 when the run doesn't exist or belongs to another user.
 */

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/api-client';
import { useAuth } from '../store/auth';

export interface TextResultData {
  title: string | null;
  markdown: string;
  chatSeedRef: string | null;
}

export function useTextResult(id: string): {
  data: TextResultData | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const jwt = useAuth((s) => s.jwt);

  const query = useQuery<TextResultData, Error>({
    queryKey: ['me-result', id],
    queryFn: async () => {
      if (!jwt) throw new Error('not_authenticated');
      const res = await apiFetch(`/api/v1/me/result/${id}`, jwt);
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`me_result_failed: ${res.status} ${body}`);
      }
      return (await res.json()) as TextResultData;
    },
    enabled: !!jwt && !!id,
    staleTime: 60_000,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
